import React, { useState, useEffect } from 'react';
import { useWorkforce } from '../../context/WorkforceContext';
import { toast } from 'react-toastify';
import api from '../../api/axiosConfig';
import 'react-toastify/dist/ReactToastify.css';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import NavigationBlocker from '../../components/common/NavigationBlocker';

const initialState = {
  employee_id: '',
  name: '',
  email: '',
  phone: '',
  role: '',
  department: 'Management',
  date_of_joining: new Date().toISOString().split('T')[0],
  employment_type: 'Full-Time',
  gender: 'Male',
  city: '',
  address: '',
  status: 'Active',
};

const EmployeeForm = ({ onClose, employeeToEdit }) => {
  const [formData, setFormData] = useState(initialState);
  const [roles, setRoles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const { addWorkforce, updateWorkforce } = useWorkforce();

  // Fetch Roles
  useEffect(() => {
    const fetchRoles = async () => {
        try {
            const res = await api.get('/roles');
            setRoles(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch (err) {
            console.error("Failed to load roles");
        }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    if (employeeToEdit) {
      let formattedDate = '';
      if (employeeToEdit.date_of_joining || employeeToEdit.joinDate) {
         try {
             formattedDate = new Date(employeeToEdit.date_of_joining || employeeToEdit.joinDate).toISOString().split('T')[0];
         } catch (e) {}
      }

      setFormData({
          ...initialState, 
          ...employeeToEdit, 
          name: employeeToEdit.name || employeeToEdit.employeeName,
          date_of_joining: formattedDate,
      });
      setIsEditing(true);
    } else {
      setFormData(initialState);
      setIsEditing(false);
    }
    // Reset dirty state when employee data is loaded/changed
    setIsDirty(false);
  }, [employeeToEdit]);

  const handleChange = (e) => {
    setIsDirty(true);
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
        if (isEditing) {
          await updateWorkforce(employeeToEdit.id, formData);
        } else {
          await addWorkforce(formData);
        }
        setIsDirty(false);
        setTimeout(() => onClose(), 800);
    } catch (error) {
        console.error("Submit Error:", error);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-6">
            {isEditing ? 'Edit Employee Details' : 'Onboard New Employee'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Employee ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Employee ID</label>
              <input type="text" name="employee_id" value={formData.employee_id} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50" />
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g., John Doe" />
            </div>

            {/* Role - Dropdown from System Roles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">System Role</label>
              <select name="role" value={formData.role} onChange={handleChange} required className="w-full px-3 py-2 border border-amber-300 bg-amber-50 rounded-md">
                <option value="">Select Role</option>
                {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <select name="department" value={formData.department || ""} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="Management">Management</option>
                <option value="IT">IT & Development</option>
                <option value="HR">Human Resources</option>
                <option value="Sales">Sales</option>
                <option value="Construction">Construction</option>
              </select>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>

            {/* Join Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date of Joining</label>
              <input type="date" name="date_of_joining" value={formData.date_of_joining} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>

            {/* Employment Type */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
                <select name="employment_type" value={formData.employment_type} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="Full-Time">Full-Time</option>
                    <option value="Part-Time">Part-Time</option>
                    <option value="Contract">Contract</option>
                </select>
            </div>

            {/* Status */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="Active">Active</option>
                <option value="On Leave">On Leave</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="pt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="bg-white text-gray-700 px-6 py-2 rounded-md border border-gray-300 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="bg-gray-900 text-white px-6 py-2 rounded-md hover:bg-gray-800">
              {isEditing ? 'Update Employee' : 'Create Employee'}
            </button>
          </div>
        </div>
      </form>

      {useUnsavedChanges(isDirty)}
      <NavigationBlocker isDirty={isDirty} />
    </>
  );
};

export default EmployeeForm;